/**
 * VOICE CALLS WITH THE INTERACTIVE ANALYSTS — React's view of
 * api/analystVoice.js.
 *
 *   useAnalystCallState()   the call as it stands (status, the analyst's
 *                           state, mic, captions, the log of final lines and
 *                           envelopes).
 *   useJoinAnalystCall(v)   runs the analyst's voice-token mutation — which
 *                           opens (or re-opens) the reader's session and links
 *                           it to a new room — then joins the room. `v` is the
 *                           analyst's descriptor: { kind, doc, field }.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { useMutation } from '@apollo/client';
import { analystCallSnapshot, onAnalystCall, startAnalystCall } from '../analystVoice';

export {
  endAnalystCall, holdAnalystCall, sendAnalystText, setAnalystMic, unlockAnalystAudio, consumeAnalystCall,
} from '../analystVoice';

export const useAnalystCallState = () => useSyncExternalStore(onAnalystCall, analystCallSnapshot);

/** Whether a call is up (or coming up) for this analyst's session. */
export const isCallFor = (call, kind, sessionId) => Boolean(sessionId)
  && call.kind === kind && Number(call.sessionId) === Number(sessionId)
  && ['connecting', 'connected', 'reconnecting'].includes(call.status);

/**
 * `join(variables)` answers the token mutation's result as soon as the room
 * token is in hand — with `analystSessionId`, the session to open — and joins
 * the room without waiting, so the session screen opens while it connects.
 */
export function useJoinAnalystCall(voice) {
  const [run, state] = useMutation(voice.doc, { fetchPolicy: 'no-cache' });
  const join = useCallback(async (variables) => {
    const { data } = await run({ variables });
    const t = data?.[voice.field];
    if (!t?.token || !t?.analystSessionId) throw new Error('The voice service sent no room or session.');
    startAnalystCall({
      token: t.token, wsUrl: t.wsUrl, room: t.room, kind: voice.kind, sessionId: t.analystSessionId,
    }).catch(() => { /* the call's own state reports it */ });
    return t;
  }, [run, voice]);
  return { join, joining: state.loading, error: state.error ?? null };
}
